import { __rest } from 'tslib';
import React, { useState, useEffect, useMemo } from 'react';
import '../../../types/AccessDevice.js';
import '../../../types/Analytics.js';
import '../../../types/CASACommunity.js';
import '../../../types/Community.js';
import '../../../types/DxSettings.js';
import '../../../types/MagicButton.js';
import '../../../types/Pannellum.js';
import '../../../types/Prospect.js';
import '../../../types/ProspectIntelligence.js';
import { SpaceType } from '../../../types/Space.js';
import '../../../types/Tour.js';
import { PannellumViewer } from './PannellumViewer.js';
import { getStartNodeId, getNodeById, getPannellumSceneConfig, identifyDevice } from './pannellumUtils.js';
import { PannellumViewerProvider } from './PannellumProvider.js';
import '../../../services/base/baseDiscoverService.js';
import '../../../services/base/baseV3Service.js';
import '../../../services/base/baseInsightsService.js';
import '../../../services/sgtService.js';
import '@analytics/storage-utils';
import 'date-fns/format';
import 'date-fns/secondsToMinutes';
import 'date-fns/isValid';
import 'date-fns/addMinutes';
import 'date-fns';
import 'date-fns-tz';
import { useError } from '../../../utils/ErrorProvider.js';
import { useLoader } from '../../../utils/LoaderProvider.js';
import { useRefetch } from '../../../utils/RefetchProvider.js';
import '../../../utils/NotificationProvider.js';
import '../../../utils/ContactProvider.js';
import '../../../utils/PolicyProvider.js';
import { useAnalytics } from '../../../utils/AnalyticsProvider.js';
import '../../../utils/analyticsPlugin.js';
import '../../../utils/communityGoogleAnalyticsPlugin.js';
import 'qs';
import '../../../utils/cookieUtils.js';
import '../../../utils/termsConditionsAgreementsUtils.js';
import '../../../services/base/analyticsV2BaseService.js';
import 'axios';
import '../../../services/base/internalApiBaseService.js';
import '../../../services/base/casaBaseService.js';
import '../../../services/scanRequestsService.js';
import '../../../services/db/insightsReportsDB.js';
import 'dot-object';
import '../../../apis/analytics/useDownloadAnalyticsReports.js';
import { useSpaceToken } from '../../../apis/space/useSpaceToken.js';
import '../../../apis/upload/useSignedURL.js';
import { PeekScreenLoader, ThemeProvider, createTheme } from '@piiqtechnologies/ui-components';
import { NavigateScene } from './NavigateScene.js';
import { useMultiResConfig } from './multiresStore.js';

const Viewer = (props) => {
    var _a;
    const { details, isDetailsLoading, isEditing } = props;
    const { children, community, communityInProgress } = props, restProps = __rest(props, ["children", "community", "communityInProgress"]);
    const [viewer, setViewer] = useState(null);
    const [viewerInstanceCreated, setViewerInstanceCreated] = useState(null);
    const { context } = useRefetch();
    const analytics = useAnalytics();
    const [getSpaceToken, spaceToken, spaceTokenInProgress, spaceTokenError] = useSpaceToken();
    const loadMultiRes = useMultiResConfig((s) => s.loadConfig);
    const loadingMultiRes = useMultiResConfig((s) => s.loadingConfigs);
    const { setError } = useError();
    const { setLoader } = useLoader();
    const onPannellumViewerLoad = (_viewer) => {
        setViewer((viewer) => (Object.assign(Object.assign(Object.assign(Object.assign({}, viewer), _viewer), restProps), { sceneId: _viewer.getScene(), getSpaceToken })));
        setViewerInstanceCreated(true);
        const isMobile = identifyDevice() === "mobile";
        if (isMobile && _viewer.isOrientationActive()) {
            _viewer.stopOrientation();
        }
    };
    const onPannellumSceneChange = (sceneId) => {
        setViewer((viewer) => (Object.assign(Object.assign({}, viewer), { sceneId })));
    };
    const onPannellumViewerSceneMovement = (config, coordinates) => {
        analytics === null || analytics === void 0 ? void 0 : analytics.track("Room View Position", {
            coordinates,
            roomId: config.scene,
            roomName: config.name,
            spaceId: details === null || details === void 0 ? void 0 : details._id,
        });
    };
    useEffect(() => {
        setLoader(spaceTokenInProgress);
    }, [spaceTokenInProgress]);
    useEffect(() => {
        if (spaceTokenError) {
            setError && setError(spaceTokenError === null || spaceTokenError === void 0 ? void 0 : spaceTokenError.message);
        }
    }, [spaceTokenError]);
    const amenities = useMemo(() => {
        if (community) {
            const { spaces = [] } = community;
            if (spaces === null || spaces === void 0 ? void 0 : spaces.length) {
                return spaces.filter((space) => space.type === SpaceType.Amenity && space.isComplete);
            }
        }
    }, [community]);
    useEffect(() => {
        if (amenities) {
            setViewer((viewer) => (Object.assign(Object.assign({}, viewer), { amenities })));
            const amenitiesNodes = [];
            amenities.forEach((amenity) => {
                if (!amenity.isMultiRes)
                    return;
                if (!amenity.nodes.length)
                    return;
                amenitiesNodes.push(...amenity.nodes);
            });
            loadMultiRes(amenitiesNodes);
        }
    }, [amenities]);
    useEffect(() => {
        setViewer((viewer) => (Object.assign(Object.assign({}, viewer), { communityInProgress })));
    }, [communityInProgress]);
    useEffect(() => {
        if (community) {
            setViewer((viewer) => (Object.assign(Object.assign({}, viewer), { community })));
        }
    }, [community]);
    useEffect(() => {
        if (details === null || details === void 0 ? void 0 : details.isMultiRes) {
            const startNodeId = getStartNodeId(details.nodes, details.startNode);
            const startNode = getNodeById(details.nodes, startNodeId);
            // Load initial node first
            loadMultiRes([startNode]).then(() => {
                // Load rest of the nodes
                loadMultiRes(details.nodes.filter((node) => node._id !== startNodeId));
            });
        }
        setViewer(null);
    }, [details === null || details === void 0 ? void 0 : details._id]);
    if (!details)
        return null;
    let node = null;
    const nodeId = (viewer === null || viewer === void 0 ? void 0 : viewer.sceneId) || (context === null || context === void 0 ? void 0 : context.sceneId) || getStartNodeId(details.nodes, details.startNode);
    node = getNodeById(details.nodes, nodeId);
    const multiresLoaded = loadingMultiRes[nodeId] === false && (!(node === null || node === void 0 ? void 0 : node.stagedPhoto) || loadingMultiRes[nodeId + "_staged"] === false);
    if (isDetailsLoading || ((details === null || details === void 0 ? void 0 : details.isMultiRes) && !multiresLoaded)) {
        return React.createElement(PeekScreenLoader, { layered: true });
    }
    const firstScene = getPannellumSceneConfig({
        node,
        isMultires: details.isMultiRes,
        isHorizonLevel: details.isHorizonLevel,
        relatedContext: {
            id: details._id,
            isEditing,
        },
        helpers: {
            clickEventAnalytics: (clickedEleName, customTags) => analytics === null || analytics === void 0 ? void 0 : analytics.track(clickedEleName, customTags),
        },
        brandingColor: (_a = details.community) === null || _a === void 0 ? void 0 : _a.secondaryColor,
        staged: true,
    });
    const sceneId = node === null || node === void 0 ? void 0 : node._id;
    return (React.createElement(ThemeProvider, { theme: (theme) => {
            var _a;
            return createTheme(Object.assign(Object.assign({}, theme), { palette: Object.assign(Object.assign({}, theme.palette), { secondary: Object.assign(Object.assign({}, theme.palette.secondary), { main: ((_a = details.community) === null || _a === void 0 ? void 0 : _a.secondaryColor) || theme.palette.secondary.main }) }) }));
        } },
        React.createElement(PannellumViewer, { config: {
                sceneFadeDuration: 1000,
                mouseZoom: false,
                showFullscreenCtrl: false,
                showZoomCtrl: false,
                showControls: false,
                autoLoad: true,
                compass: false,
                firstScene: sceneId,
                scenes: { [sceneId]: firstScene },
            }, onLoad: onPannellumViewerLoad, onSceneChange: onPannellumSceneChange, onAnimateFinished: onPannellumViewerSceneMovement, viewerKey: `pannellumContainer_${(isEditing && "editing") || "viewer"}_mode}` }),
        viewerInstanceCreated && viewer && (React.createElement(PannellumViewerProvider, { value: viewer },
            children,
            React.createElement(NavigateScene, { spaceToken: spaceToken })))));
};

export { Viewer, Viewer as default };
